<template>
    <div>
        <table class="table w-full">
            <thead>
                <tr class="h-16">
                    <sortable-header @sort="handleSort" sort-by="id" :direction="sort_direction" :column="sort_column" class="text-left border-b-2 border-gray py-2 px-2">Order #</sortable-header>
                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Customer</sortable-header>
                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Pickers</sortable-header>
                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Checkers</sortable-header>
                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Loaders</sortable-header>
                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Driver</sortable-header>

                    <template v-for="column in returnReasonCols" :key="column.label">
                        <sortable-header 
                            @sort="handleSort" 
                            :sort-by="column.sortBy" 
                            :direction="sort_direction" 
                            :column="sort_column" 
                            class="text-left border-b-2 border-gray py-2 px-2 relative"
                        > 
                            <div class="relative" 
                                @mouseenter="showToolTipCol = column.label" 
                                @mouseleave="showToolTipCol = ''"
                            >
                                {{ column.label }}
                                <jet-tooltip v-if="showToolTipCol === column.label">
                                    {{ column.tooltip }}
                                </jet-tooltip>
                            </div>
                        </sortable-header>
                    </template>

                    <sortable-header class="text-left border-b-2 border-gray py-2 px-2">Total</sortable-header>
                    <sortable-header @sort="handleSort" sort-by="refund_amount" :direction="sort_direction" :column="sort_column" class="text-left border-b-2 border-gray py-2 px-2">Refunded</sortable-header>
                </tr>
            </thead>
            <tbody>
                <tr v-for="order in data.orders" :key="order.id">
                    <td>
                        <a class="block px-2 py-1 hover:font-bold text-indigo-600" target="_blank" :href="route('orders.show', order.id)">
                            {{ order.order_number }}
                        </a>
                    </td>
                    <td> {{ order.customer_name ?? order.retail_customer_name }} </td>
                    <td> {{ order.pickers }} </td>
                    <td> {{ order.checkers }} </td>
                    <td> {{ order.loaders }} </td>
                    <td> {{ order.driver }} </td>
                    <td :class="defectCountClass(order.visible_poor_quality_count)"> {{ formatDefectCount(order.visible_poor_quality_count) }} </td>
                    <td :class="defectCountClass(order.non_visible_poor_quality_count)"> {{ formatDefectCount(order.non_visible_poor_quality_count) }} </td>
                    <td :class="defectCountClass(order.missing_item_count)"> {{ formatDefectCount(order.missing_item_count) }} </td>
                    <td :class="defectCountClass(order.wrong_item_count)"> {{ formatDefectCount(order.wrong_item_count) }} </td>
                    <td :class="defectCountClass(order.order_entry_error_count)"> {{ formatDefectCount(order.order_entry_error_count) }} </td>
                    <td :class="defectCountClass(order.driver_error_count)"> {{ formatDefectCount(order.driver_error_count) }} </td>
                    <td :class="defectCountClass(order.damaged_product_count)"> {{ formatDefectCount(order.damaged_product_count) }} </td>
                    <td :class="defectCountClass(order.runner_error_count)"> {{ formatDefectCount(order.runner_error_count) }} </td>
                    <td :class="defectCountClass(order.other_count)"> {{ formatDefectCount(order.other_count) }} </td>
                    <td> ${{ order.total }} </td>
                    <td> ${{ order.refund_amount }} </td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td class="text-left" colspan="3">
                        Total Defects: {{ data.orders.length }}
                        <template v-if="showPickerCheckerFooter">
                            · Orders checked: {{ data.orders_checked }}
                            <template v-if="data.item_defect_percent != null">
                                · Defect %: {{ formatDefectPercent(data.item_defect_percent) }}
                            </template>
                        </template>
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td class="text-left" :class="defectCountClass(data.totalVisiblePoorQuality)">{{ formatDefectCount(data.totalVisiblePoorQuality) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalNonVisiblePoorQuality)">{{ formatDefectCount(data.totalNonVisiblePoorQuality) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalMissingItems)">{{ formatDefectCount(data.totalMissingItems) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalWrongItems)">{{ formatDefectCount(data.totalWrongItems) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalOrderEntryErrors)">{{ formatDefectCount(data.totalOrderEntryErrors) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalDriverError)">{{ formatDefectCount(data.totalDriverError) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalDamagedProduct)">{{ formatDefectCount(data.totalDamagedProduct) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalRunnerError)">{{ formatDefectCount(data.totalRunnerError) }}</td>  
                    <td class="text-left" :class="defectCountClass(data.totalOther)">{{ formatDefectCount(data.totalOther) }}</td>  
                    <td class="text-left">${{ data.totalOrderValue.toFixed(2) }}</td>  
                    <td class="text-left">${{ data.totalRefundAmount.toFixed(2) }}</td>   
                </tr>
            </tfoot>
        </table>
        <p class="mt-2 px-2 text-xs leading-snug text-gray-500">
            Total Defects is the number of orders in this table for the selected dates and filters.
            <template v-if="showPickerCheckerFooter">
                <br>
                Orders checked is how many distinct orders had at least one item for the selected picker or checker in that range. Defect % is Total Defects divided by orders checked.
            </template>
        </p>
    </div>
</template>

<script>
import SortableHeader from '@/Shared/SortableHeader';
import { Link } from '@inertiajs/inertia-vue3';
import JetTooltip from '@/Jetstream/Tooltip';

export default {
    components: {
        SortableHeader,
        Link,
        JetTooltip,
    },

    props: ['data', 'sort_column', 'sort_direction'],
    emits: ['sort'],

    computed: {
        showPickerCheckerFooter() {
            return this.data.orders_checked != null;
        },
    },

    data() {
        return {
            showToolTipCol: "", //name of column showing tooltip 
            returnReasonCols: [
                { label: 'PQ-V', sortBy: 'visible_poor_quality_count', tooltip: 'Poor Quality – Visible' },
                { label: 'PQ-NV', sortBy: 'non_visible_poor_quality_count', tooltip: 'Poor Quality – Non-Visible' },
                { label: 'MI', sortBy: 'missing_item_count', tooltip: 'Missing Item' },
                { label: 'WI', sortBy: 'wrong_item_count', tooltip: 'Wrong Item' },
                { label: 'OE', sortBy: 'order_entry_error_count', tooltip: 'Order Entry Error' },
                { label: 'DE', sortBy: 'driver_error_count', tooltip: 'Driver Error' },
                { label: 'DP', sortBy: 'damaged_product_count', tooltip: 'Damaged Product' },
                { label: 'RE', sortBy: 'runner_error_count', tooltip: 'Runner Error' },
                { label: 'O', sortBy: 'other_count', tooltip: 'Other' }
            ]
        };
    },

    methods: {
        handleSort (sort) {
            this.$emit('sort', sort);
        },

        formatDefectCount(count) {
            return count == 0 ? '-' : count;
        },

        defectCountClass(count) {
            return count == 0 ? null : 'text-red-500';
        },

        formatDefectPercent(value) {
            return `${Number(value).toFixed(2)}%`;
        },
    },

}
</script>